// Monaco Code Editor Component - Full TypeScript IntelliSense + Go to Definition Support
// Install: npm install @monaco-editor/react monaco-editor
//
// Key fix: ALL models (including the active file) use file:/// URIs so Monaco's
// TypeScript worker can resolve definitions across files in the same namespace.
// We do NOT use @monaco-editor/react's `path` or `value` props — instead we
// manage models directly via editor.setModel().

import React, { useRef, useEffect, useCallback } from 'react';

// Try to import Monaco Editor
let Editor: any = null;
let MonacoAvailable = false;

try {
  const MonacoEditor = require('@monaco-editor/react');
  Editor = MonacoEditor.default || MonacoEditor.Editor;
  MonacoAvailable = true;
} catch (e) {
  console.warn('Monaco Editor not installed. Install with: npm install @monaco-editor/react monaco-editor');
  MonacoAvailable = false;
}

interface MonacoCodeEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  language?: string;
  filePath?: string;
  readOnly?: boolean;
  height?: string;
  minimap?: boolean;
  allFiles?: { [path: string]: string };
}

// Theme definition extracted to avoid repetition
const THEME_NAME = 'letsboostup-dark';

const THEME_RULES = [
  { token: 'comment', foreground: '6A9955', fontStyle: 'italic' },
  { token: 'keyword', foreground: 'C586C0', fontStyle: 'bold' },
  { token: 'storage.type', foreground: 'C586C0', fontStyle: 'bold' },
  { token: 'storage.modifier', foreground: 'C586C0', fontStyle: 'bold' },
  { token: 'string', foreground: 'CE9178' },
  { token: 'string.escape', foreground: 'CE9178' },
  { token: 'number', foreground: 'B5CEA8' },
  { token: 'number.hex', foreground: 'B5CEA8' },
  { token: 'type', foreground: '4EC9B0' },
  { token: 'type.identifier', foreground: '4EC9B0' },
  { token: 'class', foreground: '4EC9B0' },
  { token: 'function', foreground: 'DCDCAA' },
  { token: 'method', foreground: 'DCDCAA' },
  { token: 'variable', foreground: '9CDCFE' },
  { token: 'identifier', foreground: '9CDCFE' },
  { token: 'parameter', foreground: '9CDCFE' },
  { token: 'constant', foreground: '4FC1FF' },
  { token: 'constant.numeric', foreground: 'B5CEA8' },
  { token: 'property', foreground: '9CDCFE' },
  { token: 'operator', foreground: 'D4D4D4' },
  { token: 'delimiter', foreground: 'D4D4D4' },
  { token: 'tag', foreground: '569CD6' },
  { token: 'attribute.name', foreground: '92C5F7' },
  { token: 'attribute.value', foreground: 'CE9178' },
  { token: 'regexp', foreground: 'D16969' },
];

const THEME_COLORS: Record<string, string> = {
  'editor.background': '#1e1e1e',
  'editor.foreground': '#d4d4d4',
  'editorLineNumber.foreground': '#858585',
  'editorLineNumber.activeForeground': '#c6c6c6',
  'editor.selectionBackground': '#264f78',
  'editor.selectionHighlightBackground': '#add6ff26',
  'editor.lineHighlightBackground': '#2a2d2e',
  'editorCursor.foreground': '#aeafad',
  'editorWhitespace.foreground': '#3b3a32',
  'editorIndentGuide.activeBackground': '#707070',
  'editorIndentGuide.background': '#404040',
  'editor.findMatchBackground': '#515c6a',
  'editor.findMatchHighlightBackground': '#ea5c0055',
  'editorBracketMatch.background': '#0064001a',
  'editorBracketMatch.border': '#888888',
  'editorWidget.background': '#252526',
  'editorWidget.border': '#454545',
  'editorSuggestWidget.background': '#252526',
  'editorSuggestWidget.border': '#454545',
  'editorSuggestWidget.selectedBackground': '#2a2d2e',
  'editorHoverWidget.background': '#252526',
  'editorHoverWidget.border': '#454545',
  'editorError.foreground': '#f48771',
  'editorWarning.foreground': '#cca700',
  'editorInfo.foreground': '#75beff',
  'statusBar.background': '#007acc',
  'statusBar.foreground': '#ffffff',
};

const THEME_DEFINITION = {
  base: 'vs-dark' as const,
  inherit: true,
  rules: THEME_RULES,
  colors: THEME_COLORS,
};

/**
 * Normalize a file path to a consistent file:/// URI string.
 * Always produces: file:///path/to/file.ts (no double slashes beyond the scheme).
 */
function toFileUri(path: string): string {
  const normalized = path.startsWith('/') ? path.slice(1) : path;
  return `file:///${normalized}`;
}

/**
 * Detect the Monaco language ID from a file extension.
 */
function detectLanguage(filePath: string): string {
  const ext = filePath.split('.').pop()?.toLowerCase() ?? '';
  switch (ext) {
    case 'ts':
    case 'tsx':
      return 'typescript';
    case 'js':
    case 'jsx':
      return 'javascript';
    case 'json':
      return 'json';
    case 'html':
      return 'html';
    case 'css':
      return 'css';
    default:
      return 'plaintext';
  }
}

/**
 * Check if a file extension is a TS/JS file that the language service cares about.
 */
function isScriptFile(filePath: string): boolean {
  const ext = filePath.split('.').pop()?.toLowerCase() ?? '';
  return ['ts', 'tsx', 'js', 'jsx'].includes(ext);
}

const MonacoCodeEditor: React.FC<MonacoCodeEditorProps> = ({
  value = '',
  onChange,
  language = 'typescript',
  filePath = '',
  readOnly = false,
  height = '100%',
  minimap = true,
  allFiles = {}
}) => {
  const editorRef = useRef<any>(null);
  const monacoRef = useRef<any>(null);
  const activeFilePathRef = useRef<string>(filePath);
  const isNavigatingRef = useRef<boolean>(false);
  const onChangeRef = useRef(onChange);
  const contentListenerRef = useRef<any>(null);

  // Keep onChange ref current to avoid stale closures
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Keep active path ref in sync
  useEffect(() => {
    activeFilePathRef.current = filePath;
  }, [filePath]);

  // Sync to WebContainer when value changes
  useEffect(() => {
    if (!filePath || !value || !MonacoAvailable) return;

    const syncToWebContainer = async () => {
      try {
        const { getWebContainer } = await import('../../services/webcontainer');
        const wc = await getWebContainer();
        const cleanPath = filePath.startsWith('/') ? filePath.slice(1) : filePath;
        await wc.fs.writeFile(cleanPath, value);
      } catch {
        // Silently fail — WebContainer might not be ready
      }
    };

    const timeoutId = setTimeout(syncToWebContainer, 500);
    return () => clearTimeout(timeoutId);
  }, [value, filePath]);

  /**
   * Get or create a model for a given file path + content.
   * All models live under file:/// URIs for unified namespace.
   */
  const getOrCreateModel = useCallback((monaco: any, path: string, content: string): any => {
    const uri = monaco.Uri.parse(toFileUri(path));
    const existing = monaco.editor.getModel(uri);
    if (existing) {
      return existing;
    }
    const lang = detectLanguage(path);
    return monaco.editor.createModel(content, lang, uri);
  }, []);

  /**
   * Register a file with the TypeScript/JavaScript language service via addExtraLib.
   * This provides IntelliSense (hover, autocomplete) in addition to Go to Definition.
   */
  const registerFileWithLanguageService = useCallback((monaco: any, path: string, content: string) => {
    if (!isScriptFile(path)) return;

    const uri = toFileUri(path);

    try {
      monaco.languages.typescript.typescriptDefaults.addExtraLib(content, uri);
      monaco.languages.typescript.javascriptDefaults.addExtraLib(content, uri);

      // For .ts files, also register under .js URI (ESM convention: imports use .js extension)
      if (path.endsWith('.ts')) {
        const jsUri = uri.replace(/\.ts$/, '.js');
        monaco.languages.typescript.typescriptDefaults.addExtraLib(content, jsUri);
        monaco.languages.typescript.javascriptDefaults.addExtraLib(content, jsUri);
      }
    } catch (e) {
      console.warn(`Failed to register file with language service: ${uri}`, e);
    }
  }, []);

  /**
   * Sync all workspace file models. Creates new models for new files,
   * updates content of existing models if changed.
   */
  const syncAllFileModels = useCallback((monaco: any, files: Record<string, string>) => {
    Object.entries(files).forEach(([path, content]) => {
      // Get or create model
      const model = getOrCreateModel(monaco, path, content);

      // Update content if it differs (for non-active files)
      if (path !== activeFilePathRef.current && model.getValue() !== content) {
        model.setValue(content);
      }

      // Register with language service for IntelliSense
      registerFileWithLanguageService(monaco, path, content);
    });
  }, [getOrCreateModel, registerFileWithLanguageService]);

  /**
   * Attach a content change listener to the active model so we can
   * propagate edits back via onChange.
   */
  const attachContentListener = useCallback((model: any) => {
    // Dispose previous listener
    if (contentListenerRef.current) {
      contentListenerRef.current.dispose();
      contentListenerRef.current = null;
    }

    contentListenerRef.current = model.onDidChangeContent(() => {
      if (onChangeRef.current) {
        onChangeRef.current(model.getValue());
      }
    });
  }, []);

  // Effect: switch model when filePath changes, update model content when value changes
  useEffect(() => {
    const editor = editorRef.current;
    const monaco = monacoRef.current;
    if (!editor || !monaco || !filePath) return;

    const model = getOrCreateModel(monaco, filePath, value);
    const currentModel = editor.getModel();

    // Switch model if filePath changed
    if (currentModel !== model) {
      isNavigatingRef.current = true;
      editor.setModel(model);
      isNavigatingRef.current = false;
      attachContentListener(model);
    } else {
      // Same model — update content only if it differs (avoids cursor jumps)
      if (model.getValue() !== value) {
        // Preserve cursor position
        const position = editor.getPosition();
        model.setValue(value);
        if (position) {
          editor.setPosition(position);
        }
      }
    }
  }, [filePath, value, getOrCreateModel, attachContentListener]);

  // Effect: sync all workspace file models when allFiles changes
  useEffect(() => {
    const monaco = monacoRef.current;
    if (!monaco || !allFiles || Object.keys(allFiles).length === 0) return;

    syncAllFileModels(monaco, allFiles);
  }, [allFiles, syncAllFileModels]);

  // Fallback textarea when Monaco is not installed
  if (!Editor || !MonacoAvailable) {
    return (
      <div className="h-full w-full bg-[#1e1e1e] p-4">
        <div className="mb-4 p-4 bg-yellow-900/20 border border-yellow-700 rounded text-yellow-300 text-sm">
          <p className="font-semibold mb-2">⚠️ Monaco Editor Not Installed</p>
          <p className="mb-2">For proper cursor sync and TypeScript IntelliSense, install:</p>
          <code className="bg-gray-800 px-2 py-1 rounded block mb-2">npm install @monaco-editor/react monaco-editor</code>
          <p className="text-xs text-yellow-400">Using basic textarea editor below.</p>
        </div>
        <textarea
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          readOnly={readOnly}
          className="w-full h-[calc(100%-120px)] bg-[#1e1e1e] text-white font-mono text-sm p-4 border border-gray-700 rounded outline-none resize-none"
          style={{ tabSize: 2 }}
          placeholder={filePath ? `// ${filePath}` : '// Start coding...'}
        />
      </div>
    );
  }

  const handleEditorDidMount = (editor: any, monaco: any) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    // 1. Define and apply theme
    monaco.editor.defineTheme(THEME_NAME, THEME_DEFINITION);
    monaco.editor.setTheme(THEME_NAME);

    // 2. Configure TypeScript compiler options
    monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
      target: monaco.languages.typescript.ScriptTarget.ES2020,
      allowNonTsExtensions: true,
      moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
      module: monaco.languages.typescript.ModuleKind.ESNext,
      noEmit: true,
      esModuleInterop: true,
      jsx: monaco.languages.typescript.JsxEmit.React,
      reactNamespace: 'React',
      allowJs: true,
      allowSyntheticDefaultImports: true,
      skipLibCheck: true,
      strict: false,
      typeRoots: ['node_modules/@types'],
      resolveJsonModule: true,
      baseUrl: '.',
      paths: {
        '*': ['*', 'src/*', 'src/**/*']
      }
    });

    monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
      target: monaco.languages.typescript.ScriptTarget.ES2020,
      allowNonTsExtensions: true,
      moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
      module: monaco.languages.typescript.ModuleKind.ESNext,
      allowJs: true,
      allowSyntheticDefaultImports: true,
      skipLibCheck: true,
      baseUrl: '.',
      paths: {
        '*': ['*', 'src/*', 'src/**/*']
      }
    });

    // 3. Configure diagnostics — enable semantic validation for Go to Definition
    monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
      noSemanticValidation: false,
      noSyntaxValidation: false,
      noSuggestionDiagnostics: false,
      diagnosticCodesToIgnore: [8006, 8010, 2304, 2305, 2307, 2552]
    });

    // 4. Add Node.js type declarations
    const nodeTypes = `
      declare namespace NodeJS {
        interface ProcessEnv {
          [key: string]: string | undefined;
          PORT?: string;
          NODE_ENV?: string;
        }
        interface Process {
          env: ProcessEnv;
          exit(code?: number): never;
          [key: string]: any;
        }
      }
      declare var process: NodeJS.Process;
      declare var __dirname: string;
      declare var __filename: string;
      declare var console: {
        log(...args: any[]): void;
        error(...args: any[]): void;
        warn(...args: any[]): void;
        info(...args: any[]): void;
        debug(...args: any[]): void;
      };
      declare function Number(value: string | number | undefined | null): number;
    `;

    const expressTypes = `
      declare module 'express' {
        export interface Request {
          body: any;
          params: any;
          query: any;
          headers: any;
          method: string;
          url: string;
          [key: string]: any;
        }
        export interface Response {
          json(body?: any): Response;
          send(body?: any): Response;
          status(code: number): Response;
          header(field: string, value?: string): Response;
          sendStatus(code: number): Response;
          [key: string]: any;
        }
        export type NextFunction = (err?: any) => void;
        export interface Application {
          use(...handlers: any[]): Application;
          get(path: string, ...handlers: any[]): Application;
          post(path: string, ...handlers: any[]): Application;
          put(path: string, ...handlers: any[]): Application;
          delete(path: string, ...handlers: any[]): Application;
          listen(port: number, callback?: () => void): any;
          [key: string]: any;
        }
        interface ExpressStatic {
          (): Application;
          json(): any;
          urlencoded(options?: { extended?: boolean }): any;
          static(root: string): any;
          Router(): any;
        }
        const express: ExpressStatic;
        export default express;
        export { Request, Response, NextFunction };
      }
    `;

    monaco.languages.typescript.typescriptDefaults.addExtraLib(
      nodeTypes,
      'file:///node_modules/@types/node/index.d.ts'
    );
    monaco.languages.typescript.javascriptDefaults.addExtraLib(
      nodeTypes,
      'file:///node_modules/@types/node/index.d.ts'
    );
    monaco.languages.typescript.typescriptDefaults.addExtraLib(
      expressTypes,
      'file:///node_modules/@types/express/index.d.ts'
    );
    monaco.languages.typescript.javascriptDefaults.addExtraLib(
      expressTypes,
      'file:///node_modules/@types/express/index.d.ts'
    );

    // 5. Create models for ALL workspace files under file:/// URIs
    if (allFiles && Object.keys(allFiles).length > 0) {
      syncAllFileModels(monaco, allFiles);
    }

    // 6. Set the active file's model on the editor
    if (filePath) {
      const activeModel = getOrCreateModel(monaco, filePath, value);
      editor.setModel(activeModel);
      attachContentListener(activeModel);
    }

    // 7. Register onDidChangeModel listener for Go to Definition navigation
    editor.onDidChangeModel((e: any) => {
      // If we triggered this programmatically (filePath change), skip
      if (isNavigatingRef.current) return;

      const newModel = editor.getModel();
      if (!newModel) return;

      const modelPath = newModel.uri.path;
      // Normalize path for comparison
      const normalizedModelPath = modelPath.startsWith('/') ? modelPath : `/${modelPath}`;
      const normalizedActivePath = activeFilePathRef.current.startsWith('/')
        ? activeFilePathRef.current
        : `/${activeFilePathRef.current}`;

      if (normalizedModelPath !== normalizedActivePath) {
        // Monaco navigated to a different file via Go to Definition
        window.dispatchEvent(new CustomEvent('monaco-navigate-to-file', {
          detail: { path: normalizedModelPath }
        }));
      }
    });
  };

  return (
    <div className="h-full w-full">
      <Editor
        height={height}
        language={language === 'typescript' || language === 'ts' || language === 'tsx' ? 'typescript' : language}
        onMount={handleEditorDidMount}
        theme={THEME_NAME}
        beforeMount={(monaco: any) => {
          monaco.editor.defineTheme(THEME_NAME, THEME_DEFINITION);
        }}
        options={{
          readOnly,
          minimap: { enabled: minimap },
          fontSize: 14,
          lineNumbers: 'on',
          roundedSelection: false,
          scrollBeyondLastLine: false,
          automaticLayout: true,
          tabSize: 2,
          wordWrap: 'on',
          formatOnPaste: true,
          formatOnType: true,
          suggestOnTriggerCharacters: true,
          quickSuggestions: {
            other: true,
            comments: true,
            strings: true
          },
          acceptSuggestionOnCommitCharacter: true,
          acceptSuggestionOnEnter: 'on',
          snippetSuggestions: 'top',
          tabCompletion: 'on',
          wordBasedSuggestions: 'allDocuments',
          gotoLocation: {
            multiple: 'gotoAndPeek',
            multipleDefinitions: 'gotoAndPeek',
            multipleReferences: 'gotoAndPeek',
            multipleImplementations: 'gotoAndPeek',
            multipleDeclarations: 'gotoAndPeek'
          },
          links: true,
          colorDecorators: true,
          contextmenu: true,
          suggest: {
            showKeywords: true,
            showSnippets: true,
            showClasses: true,
            showFunctions: true,
            showVariables: true,
            showModules: true,
            showProperties: true,
            showEvents: true,
            showOperators: true,
            showUnits: true,
            showValues: true,
            showColors: true,
            showFiles: true,
            showReferences: true,
            showFolders: true,
            showTypeParameters: true,
            showIssues: true,
            showUsers: true,
            showSymbols: true
          }
        }}
      />
    </div>
  );
};

export default MonacoCodeEditor;
